import { User } from 'firebase/auth';
import { Mock } from 'vitest';
import { Location } from 'react-router-dom';
import { CountryCode } from 'libphonenumber-js';
import { IRole } from '../TeamMember/types';

export interface SignInPayload {
  email: string;
  password: string;
  provider?: 'microsoft';
  idToken?: string;
  accessToken?: string;
}

export enum UserRole {
  ADMIN = 'ADMIN',
  AGENT = 'AGENT',
  FINANCE = 'FINANCE',
  OPERATIONS = 'OPERATIONS',
}

export interface AgencyPermissions {
  EDIT_AGENCY_DETAILS: boolean;
  VIEW_AGENCY_DETAILS: boolean;
}

export interface TravelAgentsPermissions {
  ADD_TRAVEL_AGENTS: boolean;
  ASSIGN_CUSTOMER: boolean;
  BULK_UPLOAD_TRAVEL_AGENTS: boolean;
  DEACTIVATE_TRAVEL_AGENTS: boolean;
  EDIT_TRAVEL_AGENTS: boolean;
  VIEW_TRAVEL_AGENTS: boolean;
}

export interface BannersPermissions {
  ADD_BANNERS: boolean;
  DELETE_BANNERS: boolean;
  EDIT_BANNERS: boolean;
  VIEW_BANNERS: boolean;
}

export interface CustomersPermissions {
  ADD_CUSTOMERS: boolean;
  ASSIGN_AGENT: boolean;
  BULK_UPLOAD_CUSTOMERS: boolean;
  DEACTIVATE_CUSTOMERS: boolean;
  EDIT_CUSTOMERS: boolean;
  VIEW_CUSTOMERS: boolean;
}

export interface FeedbackPermissions {
  ADD_FEEDBACK: boolean;
  EDIT_FEEDBACK: boolean;
  VIEW_FEEDBACK: boolean;
}

export interface FinancialsPermissions {
  ADD_FINANCIALS: boolean;
  EDIT_FINANCIALS: boolean;
  VIEW_FINANCIALS: boolean;
}

export interface TravelersPermissions {
  ADD_TRAVELERS: boolean;
  DEACTIVATE_TRAVELERS: boolean;
  EDIT_TRAVELERS: boolean;
  VIEW_TRAVELERS: boolean;
}

export interface ReportsPermissions {
  VIEW_REPORTS: boolean;
}

export interface TripsPermissions {
  ADD_ACCOMMODATION: boolean;
  ADD_FLIGHT: boolean;
  ADD_MOBILITY: boolean;
  ADD_TRANSPORTATION: boolean;
  ADD_TRIPS: boolean;
  CANCEL_TRIPS: boolean;
  DELETE_ACCOMMODATION: boolean;
  DELETE_FLIGHT: boolean;
  DELETE_MOBILITY: boolean;
  DELETE_TRANSPORTATION: boolean;
  EDIT_ACCOMMODATION: boolean;
  EDIT_FLIGHT: boolean;
  EDIT_MOBILITY: boolean;
  EDIT_TRANSPORTATION: boolean;
  EDIT_TRIPS: boolean;
  VIEW_ACCOMMODATION: boolean;
  VIEW_BOOKINGS: boolean;
  VIEW_FLIGHT: boolean;
  VIEW_ITINERARY: boolean;
  VIEW_MOBILITY: boolean;
  VIEW_TRANSPORTATION: boolean;
  VIEW_TRIPS: boolean;
}

export interface PermissionCategory<T> {
  isVisible: boolean;
  permissions: T;
}

export interface Permissions {
  Agency: PermissionCategory<AgencyPermissions>;
  'Travel Agents': PermissionCategory<TravelAgentsPermissions>;
  Banners: PermissionCategory<BannersPermissions>;
  Customers: PermissionCategory<CustomersPermissions>;
  Feedback: PermissionCategory<FeedbackPermissions>;
  Financials: PermissionCategory<FinancialsPermissions>;
  Travelers: PermissionCategory<TravelersPermissions>;
  Reports: PermissionCategory<ReportsPermissions>;
  Trips: PermissionCategory<TripsPermissions>;
}

export type UserInfoResponse = Partial<{
  id: number;
  userId: number;
  email: string;
  ssoEnabled: boolean;
  roles: IRole[] | string[];
  agentId: number;
  agency: {
    id: number;
    companyId: number;
    agencyName: string;
    agencyLogo: string;
    currency?: string;
    currencyIso?: string;
    createdAt: string;
    updatedAt: string | null;
    deletedAt: string | null;
  };
  surName: string;
  fullName: string;
  dob: string;
  age: number;
  dateOfJoining: string | null;
  photo: string;
  employeeId: string;
  department: string;
  phoneCountryIso: string | null;
  phoneCountryCode: string;
  contactNumber: string;
  cpf: string;
  documentNumber: string | null;
  registrationNumber: string | null;
  welcomeMessage: string;
  preferredLanguage: string;
  passwordLastChanged: string;
  firstTimeLogin: boolean;
  termsAccepted: boolean;
  permissions: Permissions;
}>;

export enum PermissionActions {
  EDIT_AGENCY_DETAILS = 'EDIT_AGENCY_DETAILS',
  VIEW_AGENCY_DETAILS = 'VIEW_AGENCY_DETAILS',
  // travel agents
  ADD_TRAVEL_AGENTS = 'ADD_TRAVEL_AGENTS',
  ASSIGN_CUSTOMER = 'ASSIGN_CUSTOMER',
  BULK_UPLOAD_TRAVEL_AGENTS = 'BULK_UPLOAD_TRAVEL_AGENTS',
  DEACTIVATE_TRAVEL_AGENTS = 'DEACTIVATE_TRAVEL_AGENTS',
  EDIT_TRAVEL_AGENTS = 'EDIT_TRAVEL_AGENTS',
  VIEW_TRAVEL_AGENTS = 'VIEW_TRAVEL_AGENTS',
  // banners
  ADD_BANNERS = 'ADD_BANNERS',
  DELETE_BANNERS = 'DELETE_BANNERS',
  EDIT_BANNERS = 'EDIT_BANNERS',
  VIEW_BANNERS = 'VIEW_BANNERS',
  // customers
  ADD_CUSTOMERS = 'ADD_CUSTOMERS',
  ASSIGN_AGENT = 'ASSIGN_AGENT',
  BULK_UPLOAD_CUSTOMERS = 'BULK_UPLOAD_CUSTOMERS',
  DEACTIVATE_CUSTOMERS = 'DEACTIVATE_CUSTOMERS',
  EDIT_CUSTOMERS = 'EDIT_CUSTOMERS',
  VIEW_CUSTOMERS = 'VIEW_CUSTOMERS',
  // feedback
  ADD_FEEDBACK = 'ADD_FEEDBACK',
  EDIT_FEEDBACK = 'EDIT_FEEDBACK',
  VIEW_FEEDBACK = 'VIEW_FEEDBACK',
  // financials
  ADD_FINANCIALS = 'ADD_FINANCIALS',
  EDIT_FINANCIALS = 'EDIT_FINANCIALS',
  VIEW_FINANCIALS = 'VIEW_FINANCIALS',
  // travelers
  ADD_TRAVELERS = 'ADD_TRAVELERS',
  DEACTIVATE_TRAVELERS = 'DEACTIVATE_TRAVELERS',
  EDIT_TRAVELERS = 'EDIT_TRAVELERS',
  VIEW_TRAVELERS = 'VIEW_TRAVELERS',
  // reports
  VIEW_REPORTS = 'VIEW_REPORTS',
  // trips
  ADD_ACCOMMODATION = 'ADD_ACCOMMODATION',
  ADD_FLIGHT = 'ADD_FLIGHT',
  ADD_MOBILITY = 'ADD_MOBILITY',
  ADD_TRANSPORTATION = 'ADD_TRANSPORTATION',
  ADD_TRIPS = 'ADD_TRIPS',
  CANCEL_TRIPS = 'CANCEL_TRIPS',
  DELETE_ACCOMMODATION = 'DELETE_ACCOMMODATION',
  DELETE_FLIGHT = 'DELETE_FLIGHT',
  DELETE_MOBILITY = 'DELETE_MOBILITY',
  DELETE_TRANSPORTATION = 'DELETE_TRANSPORTATION',
  EDIT_ACCOMMODATION = 'EDIT_ACCOMMODATION',
  EDIT_FLIGHT = 'EDIT_FLIGHT',
  EDIT_MOBILITY = 'EDIT_MOBILITY',
  EDIT_TRANSPORTATION = 'EDIT_TRANSPORTATION',
  EDIT_TRIPS = 'EDIT_TRIPS',
  VIEW_ACCOMMODATION = 'VIEW_ACCOMMODATION',
  VIEW_BOOKINGS = 'VIEW_BOOKINGS',
  VIEW_FLIGHT = 'VIEW_FLIGHT',
  VIEW_ITINERARY = 'VIEW_ITINERARY',
  VIEW_MOBILITY = 'VIEW_MOBILITY',
  VIEW_TRANSPORTATION = 'VIEW_TRANSPORTATION',
  VIEW_TRIPS = 'VIEW_TRIPS',
}
export interface Country {
  id: number;
  name: string;
  isoAlpha2: string;
  isoAlpha3: string;
  isoNumeric: number;
  currencyCode: string;
  currencyName: string;
  currencySymbol: string;
  flag: string;
  regionFlag: string;
  phoneCountryCode: string;
}

export interface CountryListResponse {
  data: Country[];
  success: boolean;
  message: string;
}

export interface Token {
  accessToken: string;
  refreshToken?: string;
  email?: string;
}

export type ForgotPasswordResponse = {
  status: boolean;
};

export type ResetPasswordResponse = ForgotPasswordResponse;

export interface Status {
  status: boolean;
}
export interface ErrorResponse {
  status: string;
  message: string;
}

export interface ForgotPasswordPayload {
  email: string;
}

export interface ResetPasswordPayload {
  password: string;
  rePassword: string;
}

export interface VerifyOtpPayload {
  email: string;
  otp: string;
}
export interface RegisterAgencyPayload {
  companyName: string;
  countryId: number;
  currencyCode: string;
  currencyCodeIso: string;
  registrationNumber: string;
  address: string;
  email: string;
  name: string;
  contactNumber: string;
  password: string;
  logo?: { file: Blob };
  preferredLanguage: string;
}

export interface GenerateOtpPayload {
  agencyName: string;
  countryId: number;
  currencyCode: string;
  currencyCodeIso: string;
  registrationNumber: string;
  address: string;
  email: string;
  fullname: string;
  contactNumber: string;
  phoneCountryCode: string;
  phoneCountryIso: string;
  password: string;
  preferredLanguage: string;
}

export interface GenerateOtpResponse {
  success: boolean;
  message: string;
  data: { email: string };
  status: string;
}

export interface RegisterAgencyResponse {
  data: { companyName: string; registrationNumber: string; email: string; name: string };
  success: boolean;
  message: string;
  status: string;
}

export interface VerifyOtpResponse {
  success: boolean;
  message: string;
  status: string;
  data: {
    token: string;
    expiresIn: number;
  };
}

export interface ResendOtpPayload {
  email: string;
  fullName: string;
}

export interface ResendOtpResponse {
  success: boolean;
  message: string;
  data: {
    id: number;
    code: string;
    expiry: string;
    status: string;
  };
}

export interface AcceptTermsAndConditionsResponse {
  success: boolean;
  message: string;
  data: { email: string };
}

export interface SetNewPasswordResponse {
  success: boolean;
  message: string;
  data: { email: string };
  status: number;
}

export type sessionResponse = {
  sessionId: string;
  status: string;
};

export interface RequestPasswordResetPayload {
  email: string;
}

export interface RequestPasswordResetResponse {
  success: boolean;
  message: string;
  data: { email: string };
  status: number;
}

export interface SetNewPasswordResetPayload {
  token: string;
  newPassword: string;
  email: string;
}

export interface SetNewPasswordResetResponse {
  success: boolean;
  message: string;
  data: { email: string };
  status: number;
}

export interface VerifyOtpPasswordResetPayload {
  token: string;
  email: string;
}

export interface VerifyOtpPasswordResetResponse {
  success: boolean;
  message: string;
  data: { email: string };
  status: number;
}

export interface AuthStateCallback {
  (): void;
}

export interface AuthInstance {
  currentUser: User | null;
  onAuthStateChanged: (authInstance: AuthInstance, callback: AuthStateCallback) => () => void;
}

export interface LocationState {
  pathname: string;
  search: string;
}

export interface LocationStateWithFrom {
  from: LocationState;
}

export interface NavigateOptions {
  state: LocationStateWithFrom;
  replace: boolean;
}

export interface AuthTestProps {
  children: React.ReactNode;
}

export interface TestComponentProps {
  dataTestId: string;
  children: React.ReactNode;
}

export type MockFunction = Mock;

export interface MockUser {
  uid: string;
}

// Updated to match the actual function signature
export type MockTokenFunction = {
  mockResolvedValue: (value: string | null) => void;
  mockRejectedValue: (error: Error) => void;
} & (() => Promise<string | boolean>);

// Updated to match the actual function signature
export type MockLocationFunction = {
  mockReturnValue: (value: LocationState) => void;
} & (() => Location);

export interface CountryRegistrationResponse {
  success: boolean;
  message: string;
  data: {
    id: number;
    regularExpression: string;
    formatExample: string;
    countryId: number;
  };
  status: number;
}

export interface ContractResponse {
  success: boolean;
  message: string;
  data: {
    id: number;
    type: string;
    content: string;
    version: string;
    localeId: number;
    active: boolean;
  };
  status: number;
}

export interface LogoInfo {
  name: string;
  size: number;
  type: string;
  uid: string;
}

export interface CachedAgencyFormData {
  formValues: Omit<RegisterAgencyPayload, 'logo'>;
  selectedCountry: { code: CountryCode; iso: string | undefined };
  logoInfo?: LogoInfo;
}
